// Currency formatting utility
// Used across all pages to display monetary values with the tenant's configured currency.

// Consignes 25/09 (02.6 · 03.10) : code ISO par défaut, toujours 2 décimales, séparateurs selon le pays.
// « 83 761,53 MAD », « 2 552,00 EUR », « USD 83,761.53 ».
export const SUPPORTED_CURRENCIES = {
  EUR: { symbol: '€', name: 'Euro', position: 'after', locale: 'fr-FR' },
  USD: { symbol: 'USD', name: 'Dollar US', position: 'before', locale: 'en-US' },
  MAD: { symbol: 'MAD', name: 'Dirham marocain', position: 'after', locale: 'fr-FR' },
  GBP: { symbol: 'GBP', name: 'Livre sterling', position: 'before', locale: 'en-GB' },
  CHF: { symbol: 'CHF', name: 'Franc suisse', position: 'after', locale: 'fr-CH' },
  CAD: { symbol: 'CAD', name: 'Dollar canadien', position: 'before', locale: 'en-CA' },
  AED: { symbol: 'AED', name: 'Dirham des Émirats', position: 'after', locale: 'fr-FR' },
  TND: { symbol: 'TND', name: 'Dinar tunisien', position: 'after', locale: 'fr-FR' },
  XOF: { symbol: 'XOF', name: 'Franc CFA', position: 'after', locale: 'fr-FR' },
};
const COUNTRY_LOCALE = { US: 'en-US', GB: 'en-GB', CA: 'en-CA', CH: 'fr-CH' };
let tenantCountry = null;
/** Pays de l'établissement (réglage) : pilote séparateurs et position du code. */
export const setCurrencyCountry = (code) => { tenantCountry = code ? String(code).toUpperCase() : null; };
let tenantCurrency = 'EUR';
export const setTenantCurrency = (code) => { tenantCurrency = (code || 'EUR').toUpperCase(); };
/** Montant dans la devise de l'établissement courant (pour les écrans sans accès au contexte). */
export const money = (value, options) => formatCurrency(value, tenantCurrency, options);
/* LOT 13 · 13.2 · une seule fonction d'affichage : « 1 234,50 € » / « 1 234,50 MAD » */
export const formatMontant = (montant, devise) => formatCurrency(montant, devise || tenantCurrency);

export const getCurrencyConfig = (code) => {
  const base = SUPPORTED_CURRENCIES[code] || { symbol: (code || 'EUR').toUpperCase(), name: code, position: 'after', locale: 'fr-FR' };
  const locale = (tenantCountry && COUNTRY_LOCALE[tenantCountry]) || base.locale;
  return { ...base, locale, position: locale.startsWith('en') ? 'before' : 'after' };
};

/**
 * Format a numeric value as a currency string.
 * @param {number} value - Numeric value
 * @param {string} currencyCode - ISO code (EUR, USD, MAD, GBP, CHF, CAD)
 * @param {object} options - { compact: boolean, decimals: number }
 */
export const formatCurrency = (value, currencyCode = 'EUR', options = {}) => {
  const { decimals = 2 } = options;
  const cfg = getCurrencyConfig(currencyCode);
  const num = Number(value) || 0;
  const formatted = num.toLocaleString(cfg.locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).replace(/\u202f/g, '\u00a0');
  return cfg.position === 'before'
    ? `${cfg.symbol}\u00a0${formatted}`
    : `${formatted}\u00a0${cfg.symbol}`;
};

/** Code devise à afficher : celui passé en argument, sinon celui de l'établissement courant (jamais « EUR » par défaut). */
export const currencySymbol = (currencyCode) => getCurrencyConfig(currencyCode || tenantCurrency).symbol;

/** Cahier 26/09 · 01.7 : montant + base « HT » / « TTC » en petit après la devise (« 66 893,50 MAD TTC »). */
export const formatMoneyBase = (value, currencyCode = 'EUR', base = 'ttc', options = {}) => `${formatCurrency(value, currencyCode, options)}\u00a0${String(base || '').toUpperCase()}`.trim();
/** Sépare « 66 893,50 MAD » en [montant, code] pour afficher le code (et la base) en petit. */
export const splitMoney = (formatted) => { const s = String(formatted || ''); const i = s.lastIndexOf('\u00a0'); return i < 0 ? [s, ''] : /^([A-Z]{3}|€)$/.test(s.slice(i + 1)) ? [s.slice(0, i), s.slice(i + 1)] : /^[A-Z]{3}$/.test(s.slice(0, s.indexOf('\u00a0'))) ? [s.slice(s.indexOf('\u00a0') + 1), s.slice(0, s.indexOf('\u00a0'))] : [s, '']; };

/** Consignes 25/09 (02.9) : date au format du pays de l'établissement (31/07/2026 · 07/31/2026). Jamais d'ISO à l'écran. */
export const formatDate = (value, opts = {}) => {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(/^\d{4}-\d{2}-\d{2}$/.test(String(value)) ? `${value}T12:00:00` : value);
  if (Number.isNaN(d.getTime())) return String(value);
  const locale = (tenantCountry && COUNTRY_LOCALE[tenantCountry]) || 'fr-FR';
  return d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric', ...opts });
};
